import React, {useEffect} from 'react';
import {
    Box, Alert, Typography, Fade,
    Button, Drawer, Popover, Snackbar,
    Table, TableBody, TableContainer, Paper,
} from '@mui/material';
import {SplitTabsPanel} from '@/components/common';
import {
    BalanceSheet,
    ProfitLoss,
    FsDetailShellSkeleton,
    FsStatementTableSkeletonRows,
    buildFsPlSkeletonColumns,
} from '@/pages/components/fs-report';
import {changeFs, changeStatus, importExcel} from '@/services/auditService/auditFsAnnualService';
import type {ExcelImportResult} from '@/services/auditService/auditFsAnnualService';
import {AnnualFsStatusEnum} from '@/services/auditService';
import type {BalanceSheetHandle} from '@/pages/components/fs-report/BalanceSheet';
import type {ProfitLossHandle} from '@/pages/components/fs-report/ProfitLoss';
import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {BalanceSheetOutlinedIcon, ProfitLossOutlinedIcon} from '@/assets/icons/FsStatementIcon';
import '@mui/icons-material/FileUpload';
import {
    buildPlFsInitSnapshot,
    buildPlFsWarmupSignature,
    countFsTreeNodes,
    clearPlFsWarmup,
    cancelPendingPlFsWarmup,
    peekPlFsWarmup,
    schedulePlFsWarmup,
} from '@/pages/components/fs-report/plFsMultiCompute';
import {useIdleAutoSave} from '@/hooks/useIdleAutoSave';
import {AutoSaveStatus} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/AutoSaveStatus';
import PriorYearFsExtractBar, {
    type PriorYearPeriodHint,
} from '@/pages/components/account/gl-invoice/PriorYearFsExtractBar';
import {
    mergeRemarksReplaceByFs,
    priorToFsLastYearMaps,
} from '@/pages/components/account/gl-invoice/glLastYearFs';
import {resolvePlCotInputMode} from '@/utils/plCostOfSalesVisibility';
import {
    resolvePlCotFsIds,
    zeroPlCotFormulaSideInDataMap,
} from '@/pages/components/fs-report/plFsMultiCompute';

interface FsDetailProps {
    open?: boolean;
    onClose?: () => void;
    /** 关闭/返回列表时触发，用于父页面重新拉取列表数据 */
    onRequestListRefresh?: () => void;
    companyId: number;
    annualId: number;
    companyName?: string | null;
    onUpdated?: (message?: string) => void;
    hideUpdateButton?: boolean;
    onBsErrorChange?: (message?: string | null) => void;
    /** BS-EQ 与 PL-PAT 均为 0 时回调 true，用于隐藏 Submit */
    onHideSubmitChange?: (hide: boolean) => void;
    importToken?: string | null;
}

const PANEL_HEIGHT = 'calc(100vh - 260px)';
/** Drawer 模式下由父级 flex 撑满剩余视口高度 */
const DRAWER_FILL_HEIGHT = '100%';

/** Drawer 布局固定宽度（与 SplitTabsPanel / BS·PL fs 模式表格对齐） */
const DRAWER_LEFT_PANE_PX = 208; // tabs 200 + gap
const DRAWER_CONTENT_PAD_PX = 48; // Drawer content p:2 + table 内边距
const DRAWER_AMOUNT_COL_PX = 160;
const DRAWER_CHANGE_COL_PX = 120;
const DRAWER_ROW_ICON_PX = 42; // expand 按钮 + Typography mr
const DRAWER_INDENT_PER_LEVEL_PX = 16; // pl: level * 2 → 16px
const DRAWER_CHROME_EXTRA_PX = 32; // 滚动条 / 边框缓冲
const DRAWER_MIN_WIDTH_PX = 640;
/** 自适应宽度低于视口该比例时，按该比例渲染；高于时按内容自适应扩展 */
const DRAWER_MIN_WIDTH_RATIO = 0.5;
const DRAWER_MAX_WIDTH_RATIO = 0.95;
/** MoneyField / InputField：MUI Outlined small 左右 padding(~14*2) + border + 单元格余量 */
const DRAWER_AMOUNT_INPUT_PAD_PX = 48;
/**
 * SINGLE：PL Current / Last Year 单元格在 MoneyField 外侧有 COT SwapHoriz（gap 0.5 + small IconButton）。
 * 金额列下限需预留该宽度，否则 BS 同列仍按纯 MoneyField 定宽，切换 Tab 时与 PL 初始化列宽不一致。
 */
const DRAWER_AMOUNT_TRAILING_ACTION_PX = 28;
/** canvas 测量相对真实渲染常偏低，补偿系数避免 ".00" 被裁切 */
const DRAWER_AMOUNT_WIDTH_SAFETY = 1.18;
const DRAWER_AMOUNT_SUBPIXEL_EXTRA_PX = 6;
const DRAWER_AMOUNT_FONT = '400 0.875rem Roboto, Helvetica, Arial, sans-serif';

type FsNodeLabel = {text: string; depth: number; level?: number};

function collectFsNodeLabels(nodes: any[] | undefined, depth = 0, out: FsNodeLabel[] = []): FsNodeLabel[] {
    if (!Array.isArray(nodes)) return out;
    for (const n of nodes) {
        const text = String(
            (n as any)?.aliasEn || (n as any)?.nameEn || n?.name || (n as any)?.alias || n?.code || ''
        ).trim();
        if (text) {
            out.push({text, depth, level: (n as any)?.level});
        }
        if (Array.isArray(n?.children) && n.children.length > 0) {
            collectFsNodeLabels(n.children, depth + 1, out);
        }
    }
    return out;
}

function measureTextWidthPx(text: string, font: string): number {
    if (!text) return 0;
    if (typeof document === 'undefined') {
        return text.length * 8;
    }
    // Reuse one canvas — creating per call was costly when measuring whole FS trees
    let canvas = (measureTextWidthPx as any)._canvas as HTMLCanvasElement | undefined;
    if (!canvas) {
        canvas = document.createElement('canvas');
        (measureTextWidthPx as any)._canvas = canvas;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * 8;
    ctx.font = font;
    return ctx.measureText(text).width;
}

function measureFsLabelWidth(text: string, level?: number): number {
    // 与 BalanceSheet / ProfitLoss 节点名排版一致
    const font = level === 2
        ? '600 14px "Times New Roman", Times, serif'
        : '400 14px Roboto, Helvetica, Arial, sans-serif';
    return measureTextWidthPx(text, font);
}

function formatMoneyDisplayForMeasure(num: number): string {
    const formatter = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return num < 0 ? `(${formatter.format(Math.abs(num))})` : formatter.format(num);
}

/**
 * 金额文本宽度：始终按最终展示形态（千分位 + 两位小数 ".00"）取较大值，
 * 避免草稿无小数/无逗号时列宽偏小，blur 格式化后裁切 ".00"。
 */
function measureAmountContentWidthPx(
    raw: string | number | null | undefined,
    font: string = DRAWER_AMOUNT_FONT,
): number {
    if (raw === null || raw === undefined || raw === '') return 0;
    const draft = String(raw).trim();
    if (!draft) return 0;

    const draftPx = measureTextWidthPx(draft, font);
    const normalized = draft.replace(/[(),\s]/g, '');
    const parseTarget = normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
    const num = Number(parseTarget);

    let formattedPx = 0;
    if (Number.isFinite(num)) {
        formattedPx = measureTextWidthPx(formatMoneyDisplayForMeasure(num), font);
    } else if (!draft.includes('.')) {
        // 无法解析时仍预留小数位，防止 ".00" 被挤出
        formattedPx = draftPx + measureTextWidthPx('.00', font);
    }

    const basePx = Math.max(draftPx, formattedPx);
    return basePx * DRAWER_AMOUNT_WIDTH_SAFETY + DRAWER_AMOUNT_SUBPIXEL_EXTRA_PX;
}

/** 使用 input 实际计算字体测量，更贴近渲染宽度 */
function measureLiveInputContentWidthPx(input: HTMLInputElement): number {
    if (!input.value) return 0;
    const style = window.getComputedStyle(input);
    const font = [
        style.fontStyle,
        style.fontWeight,
        style.fontSize,
        style.fontFamily,
    ].filter(Boolean).join(' ');
    return measureAmountContentWidthPx(input.value, font || DRAWER_AMOUNT_FONT);
}

function maxAmountContentWidthFromMaps(
    ...maps: Array<Record<number, string> | undefined | null>
): number {
    let maxPx = 0;
    for (const map of maps) {
        if (!map) continue;
        for (const v of Object.values(map)) {
            maxPx = Math.max(maxPx, measureAmountContentWidthPx(v));
        }
    }
    return maxPx;
}

function computeFsDetailDrawerWidthPx(args: {
    bsTree: any[] | undefined;
    plTree: any[] | undefined;
    hideLastYearColumns: boolean;
    viewportWidth: number;
    amountColPx: number;
    changeColPx: number;
    /** MULTI PL: number of YOA amount columns (>=2). SINGLE / omit = 1 Current column. */
    yoaColumnCount?: number;
}): number {
    const {bsTree, plTree, hideLastYearColumns, viewportWidth, amountColPx, changeColPx} = args;
    const yoaN = args.yoaColumnCount && args.yoaColumnCount >= 2 ? args.yoaColumnCount : 1;
    const labels = [
        ...collectFsNodeLabels(bsTree),
        ...collectFsNodeLabels(plTree),
    ];

    let maxNameColPx = 180;
    for (const {text, depth, level} of labels) {
        const textPx = measureFsLabelWidth(text, level);
        const rowNamePx = depth * DRAWER_INDENT_PER_LEVEL_PX + DRAWER_ROW_ICON_PX + textPx;
        if (rowNamePx > maxNameColPx) maxNameColPx = rowNamePx;
    }

    // fs 模式列：Name | Current×N (or YOA×N) | [Last Year | Change]
    const amountColsPx = amountColPx * yoaN
        + (hideLastYearColumns ? 0 : amountColPx + changeColPx);

    const needed = DRAWER_LEFT_PANE_PX
        + DRAWER_CONTENT_PAD_PX
        + maxNameColPx
        + amountColsPx
        + DRAWER_CHROME_EXTRA_PX;

    // 自适应宽度 < 视口 50% → 按 50% 渲染；≥ 50%（含输入加宽）→ 按内容自适应扩展
    const halfViewportPx = Math.floor(viewportWidth * DRAWER_MIN_WIDTH_RATIO);
    const adaptivePx = Math.max(DRAWER_MIN_WIDTH_PX, needed);
    const widthPx = Math.max(halfViewportPx, adaptivePx);
    const maxPx = Math.max(DRAWER_MIN_WIDTH_PX, Math.floor(viewportWidth * DRAWER_MAX_WIDTH_RATIO));
    return Math.round(Math.min(maxPx, widthPx));
}

export const FsDetail = React.forwardRef<{
    getLines: () => {
        bsLines: Array<{ fsId: number; amount: number; lastYearChangeAudited?: number }>;
        plLines: Array<{ fsId: number; amount: number; lastYearChangeAudited?: number }>
    } | null
}, FsDetailProps>(function FsDetail({
                                        open, onClose: onCloseProp, onRequestListRefresh, companyId,
                                        annualId, companyName, onUpdated,
                                        hideUpdateButton, onBsErrorChange,
                                        onHideSubmitChange,
                                        importToken
                                    }, ref) {
    const isDrawerMode = !!open;
    const contentHeight = isDrawerMode ? DRAWER_FILL_HEIGHT : PANEL_HEIGHT;
    const tabsHeight = isDrawerMode ? DRAWER_FILL_HEIGHT : `calc((${PANEL_HEIGHT}) - 40px)`;
    const [viewportWidth, setViewportWidth] = React.useState(
        () => (typeof window !== 'undefined' ? window.innerWidth : 1280)
    );
    // store-driven status & data
    const {
        loading: sLoading,
        error: sError,
        bsTree: sBSTree,
        plTree: sPLTree,
        bsValues: sBSValues,
        plValues: sPLValues,
        bsLastYearValues: sBSLastYearValues,
        plLastYearValues: sPLLastYearValues,
        bsHasLastYearChangeAudited: sBSHasLastYearChangeAudited,
        plHasLastYearChangeAudited: sPLHasLastYearChangeAudited,
        firstYearAudit: storeFirstYearAudit,
        initIfNeeded,
        cancelInFlight
    } = useFsCategoryStore();

    const plValuesByYoa = useFsCategoryStore(s => s.plValuesByYoa);

    // Also check firstAudit from companyAnnualStore as the authoritative source
    const annualFromStore = useCompanyAnnualStore(s => s.byAnnualId[annualId]);

    // Phase 3: MULTI YOA columns for FS ProfitLoss
    const [fsYoaColumns, setFsYoaColumns] = React.useState<string[] | null>(null);
    const annualPeriodKey = `${annualFromStore?.periodFrom || ''}|${annualFromStore?.periodTo || ''}|${annualFromStore?.isMultiYearAudit}`;
    React.useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const span = await useCompanyAnnualStore.getState().getAuditPeriodSpan(annualId);
                if (!mounted) return;
                if (span.type === 'MULTI' && span.slices.length >= 2) {
                    // 去重：同 YOA 双列会撞 uk_pl_annual_fs_yoa（如两列都是 2024/25）
                    const yoas = Array.from(new Set(
                        span.slices
                            .map(s => (s.yearOfAssessment || '').trim())
                            .filter((y): y is string => !!y),
                    ));
                    setFsYoaColumns(yoas.length >= 2 ? yoas : null);
                } else {
                    setFsYoaColumns(null);
                }
            } catch {
                if (mounted) setFsYoaColumns(null);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, annualPeriodKey]);

    const isFsMulti = Array.isArray(fsYoaColumns) && fsYoaColumns.length >= 2;

    const plAfterTax = useFsCategoryStore(s => s.plAfterTax);

    const firstYearAudit = storeFirstYearAudit || Number(annualFromStore?.firstAudit) === 1;

    // Ensure annual data is loaded for firstAudit check
    React.useEffect(() => {
        if (annualId > 0 && !annualFromStore) {
            useCompanyAnnualStore.getState().loadAnnual(annualId).catch(() => {});
        }
    }, [annualId, annualFromStore]);
    const loading = !!sLoading;
    // Store 就绪：API/解析完成且 BS/PL 树均已填充（组件仍可并行 init）
    const dataReady = !loading && sBSTree !== undefined && sPLTree !== undefined;
    /** BS 表内部 init 完成 → 可撤外层龙骨并先渲染 BS */
    const [bsUiReady, setBsUiReady] = React.useState(false);
    /** PL 表内部 init 完成 → 才允许点击 PL tab */
    const [plUiReady, setPlUiReady] = React.useState(false);
    const shellReady = dataReady && bsUiReady;
    const [uiError, setUiError] = React.useState<string | null>(null);
    const error = uiError ?? sError ?? null;
    const [updating, setUpdating] = React.useState(false);
    const [bsError, setBsError] = React.useState<string | null>(null);
    /** Click-time viewport position — Submit button can unmount (loading / bsStatus) while Popover is open. */
    const [submitAnchorPos, setSubmitAnchorPos] = React.useState<{top: number; left: number} | null>(null);
    const [importing, setImporting] = React.useState(false);
    const [importSnackbar, setImportSnackbar] = React.useState<{open: boolean; message: string; severity: 'success' | 'error'}>({open: false, message: '', severity: 'success'});
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    /** AI extract Last Year → bump to sync BS/PL local lastYearAmounts from store */
    const [lastYearApplyNonce, setLastYearApplyNonce] = React.useState(0);
    const [lastYearRemarkByFsCode, setLastYearRemarkByFsCode] = React.useState<Record<string, string>>({});
    const expectedLastYearPeriod = React.useMemo<PriorYearPeriodHint | undefined>(() => {
        const from = annualFromStore?.lastYearPeriodFrom || null;
        const to = annualFromStore?.lastYearPeriodTo || null;
        if (!from && !to) return undefined;
        return {
            expectedFrom: from,
            expectedTo: to,
            expectedLabel: `${from || '?'} ~ ${to || '?'}`,
        };
    }, [annualFromStore?.lastYearPeriodFrom, annualFromStore?.lastYearPeriodTo]);
    const [bsStatus, setBsStatus] = React.useState<{
        netAssets: number;
        capitalReserves: number;
        equal: boolean;
        zero: boolean;
        lastYearNetAssets?: number;
        lastYearCapitalReserves?: number;
        equalLastYear?: boolean;
        zeroLastYear?: boolean;
    }>({netAssets: 0, capitalReserves: 0, equal: true, zero: false});

    // Drawer：内容自适应宽度；低于视口 50% 时按 50% 渲染，高于时随内容/输入扩展；窗口变化时重新钳制
    useEffect(() => {
        if (!isDrawerMode) return;
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isDrawerMode]);

    // 输入过程中草稿值尚未写入 store：监听 input，按最长输入串动态加宽金额列
    const [measureRoot, setMeasureRoot] = React.useState<HTMLDivElement | null>(null);
    const contentMeasureRef = React.useCallback((node: HTMLDivElement | null) => {
        setMeasureRoot(node);
    }, []);
    const [liveAmountTextWidthPx, setLiveAmountTextWidthPx] = React.useState(0);

    useEffect(() => {
        if (!isDrawerMode || !dataReady || !measureRoot) {
            setLiveAmountTextWidthPx(0);
            return;
        }

        const measureLiveInputs = () => {
            let maxPx = 0;
            measureRoot.querySelectorAll('input').forEach((el) => {
                const input = el as HTMLInputElement;
                if (!input.value) return;
                maxPx = Math.max(maxPx, measureLiveInputContentWidthPx(input));
            });
            setLiveAmountTextWidthPx(maxPx);
        };

        // blur 后 MoneyField 会把值格式化为千分位 + ".00"，延后一帧再测避免读到草稿
        const measureAfterFormat = () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(measureLiveInputs);
            });
        };

        measureRoot.addEventListener('input', measureLiveInputs, true);
        measureRoot.addEventListener('change', measureAfterFormat, true);
        measureRoot.addEventListener('focusout', measureAfterFormat, true);
        const raf = requestAnimationFrame(measureLiveInputs);
        return () => {
            cancelAnimationFrame(raf);
            measureRoot.removeEventListener('input', measureLiveInputs, true);
            measureRoot.removeEventListener('change', measureAfterFormat, true);
            measureRoot.removeEventListener('focusout', measureAfterFormat, true);
        };
    }, [isDrawerMode, dataReady, measureRoot, sBSTree, sPLTree, firstYearAudit]);

    const storeAmountTextWidthPx = React.useMemo(() => {
        const maps: Array<Record<number, string> | undefined | null> = [
            sBSValues, sPLValues, sBSLastYearValues, sPLLastYearValues,
        ];
        if (isFsMulti && plValuesByYoa) {
            for (const m of Object.values(plValuesByYoa)) maps.push(m);
        }
        return maxAmountContentWidthFromMaps(...maps);
    }, [sBSValues, sPLValues, sBSLastYearValues, sPLLastYearValues, isFsMulti, plValuesByYoa]);

    const amountColWidthPx = React.useMemo(() => {
        // MULTI: prefer store-based width; deferred editable means few live inputs on first paint
        const contentPx = isFsMulti
            ? storeAmountTextWidthPx
            : Math.max(storeAmountTextWidthPx, liveAmountTextWidthPx);
        // SINGLE：Current / Last Year 与 PL 同列对齐（含 COT trailing action）
        const trailingPx = isFsMulti ? 0 : DRAWER_AMOUNT_TRAILING_ACTION_PX;
        return Math.max(
            DRAWER_AMOUNT_COL_PX + trailingPx,
            Math.ceil(contentPx + DRAWER_AMOUNT_INPUT_PAD_PX + trailingPx),
        );
    }, [storeAmountTextWidthPx, liveAmountTextWidthPx, isFsMulti]);

    // Change 列无 COT trailing；SINGLE 下勿被金额列 floor(160+28) 抬宽，与 PL 表头 120 对齐
    const changeColWidthPx = React.useMemo(() => {
        const contentPx = isFsMulti
            ? storeAmountTextWidthPx
            : Math.max(storeAmountTextWidthPx, liveAmountTextWidthPx);
        return Math.max(
            DRAWER_CHANGE_COL_PX,
            Math.ceil(contentPx + DRAWER_AMOUNT_INPUT_PAD_PX),
        );
    }, [isFsMulti, storeAmountTextWidthPx, liveAmountTextWidthPx]);

    const drawerWidthPx = React.useMemo(() => {
        if (!isDrawerMode) return DRAWER_MIN_WIDTH_PX;
        if (!dataReady) {
            return Math.round(Math.min(
                Math.floor(viewportWidth * DRAWER_MAX_WIDTH_RATIO),
                Math.max(DRAWER_MIN_WIDTH_PX, Math.floor(viewportWidth * DRAWER_MIN_WIDTH_RATIO)),
            ));
        }
        return computeFsDetailDrawerWidthPx({
            bsTree: sBSTree,
            plTree: sPLTree,
            hideLastYearColumns: firstYearAudit,
            viewportWidth,
            amountColPx: amountColWidthPx,
            changeColPx: changeColWidthPx,
            yoaColumnCount: isFsMulti && fsYoaColumns ? fsYoaColumns.length : 1,
        });
    }, [
        isDrawerMode,
        dataReady,
        sBSTree,
        sPLTree,
        firstYearAudit,
        viewportWidth,
        amountColWidthPx,
        changeColWidthPx,
        isFsMulti,
        fsYoaColumns,
    ]);

    // MULTI: size every YOA amount column + Last Year / Change (not just cols 2–4)
    const amountColumnCss = React.useMemo(() => {
        const yoaN = isFsMulti && fsYoaColumns ? fsYoaColumns.length : 1;
        const amountSx = {
            width: `${amountColWidthPx}px !important`,
            minWidth: `${amountColWidthPx}px !important`,
            maxWidth: 'none !important',
            boxSizing: 'border-box' as const,
        };
        const css: Record<string, typeof amountSx | {
            width: string;
            minWidth: string;
            maxWidth: string;
            boxSizing: 'border-box';
        }> = {};
        for (let i = 0; i < yoaN; i++) {
            css[`& .MuiTableCell-root:nth-of-type(${i + 2})`] = amountSx;
        }
        if (!firstYearAudit) {
            const lyNth = yoaN + 2;
            const chNth = yoaN + 3;
            css[`& .MuiTableCell-root:nth-of-type(${lyNth})`] = amountSx;
            css[`& .MuiTableCell-root:nth-of-type(${chNth})`] = {
                width: `${changeColWidthPx}px !important`,
                minWidth: `${changeColWidthPx}px !important`,
                maxWidth: 'none !important',
                boxSizing: 'border-box',
            };
        }
        return css;
    }, [isFsMulti, fsYoaColumns, amountColWidthPx, changeColWidthPx, firstYearAudit]);

    // Track initial lastYear values to detect changes
    const initialLastYearValuesRef = React.useRef<{
        bs: Record<number, string>;
        pl: Record<number, string>;
    }>({bs: {}, pl: {}});

    // Track which nodes originally had lastYearChangeAudited from backend
    const hasLastYearChangeAuditedRef = React.useRef<{
        bs: Set<number>;
        pl: Set<number>;
    }>({bs: new Set(), pl: new Set()});

    const bsRef = React.useRef<BalanceSheetHandle | null>(null);
    const plRef = React.useRef<ProfitLossHandle | null>(null);

    // Granular reset: only reset store when company/annual change
    const prevKeyRef = React.useRef<string | null>(null);
    useEffect(() => {
        const key = `${companyId}-${annualId}`;
        const prev = prevKeyRef.current;
        if (prev && prev !== key) {
            try {
                clearPlFsWarmup();
                useFsCategoryStore.getState().reset();
                // Reset initial lastYear values when switching company/annual
                initialLastYearValuesRef.current = {bs: {}, pl: {}};
                hasLastYearChangeAuditedRef.current = {bs: new Set(), pl: new Set()};
                setLastYearApplyNonce(0);
                setLastYearRemarkByFsCode({});
            } catch {
            }
        }
        prevKeyRef.current = key;
    }, [companyId, annualId]);

    // Initialize via store
    useEffect(() => {
        if (!companyId || !annualId) return;
        initIfNeeded(companyId, annualId, importToken);
        return () => {
            try {
                cancelInFlight();
            } catch {
            }
        };
    }, [companyId, annualId, companyName, importToken, initIfNeeded, cancelInFlight]);

    // Store initial lastYear values when they are loaded (ONLY ONCE)
    useEffect(() => {
        // Only set initial values if they haven't been set yet
        if (sBSLastYearValues && Object.keys(sBSLastYearValues).length > 0 && Object.keys(initialLastYearValuesRef.current.bs).length === 0) {
            initialLastYearValuesRef.current.bs = {...sBSLastYearValues};
        }
        if (sPLLastYearValues && Object.keys(sPLLastYearValues).length > 0 && Object.keys(initialLastYearValuesRef.current.pl).length === 0) {
            initialLastYearValuesRef.current.pl = {...sPLLastYearValues};
        }
        // Store which nodes have lastYearChangeAudited from backend
        if (sBSHasLastYearChangeAudited && sBSHasLastYearChangeAudited.size > 0 && hasLastYearChangeAuditedRef.current.bs.size === 0) {
            hasLastYearChangeAuditedRef.current.bs = new Set(sBSHasLastYearChangeAudited);
        }
        if (sPLHasLastYearChangeAudited && sPLHasLastYearChangeAudited.size > 0 && hasLastYearChangeAuditedRef.current.pl.size === 0) {
            hasLastYearChangeAuditedRef.current.pl = new Set(sPLHasLastYearChangeAudited);
        }
    }, [sBSLastYearValues, sPLLastYearValues, sBSHasLastYearChangeAudited, sPLHasLastYearChangeAudited]);

    // Mirror subsequent local edits/context into store for gradual prop removal
    // Mirror company context (name) if updated
    useEffect(() => {
        try {
            useFsCategoryStore.getState().setContext({companyId, companyName: companyName ?? null});
        } catch {
        }
    }, [companyId, companyName]);

    // Cleanup on unmount: reset fsEditingStore to avoid cross-company/annual flashes
    useEffect(() => {
        return () => {
            try {
                clearPlFsWarmup();
                useFsCategoryStore.getState().reset();
            } catch {
            }
        };
    }, []);

    const collectLines = React.useCallback((): {
        bsLines: Array<{ fsId: number; amount: number; lastYearChangeAudited?: number }>,
        plLines: Array<{ fsId: number; amount: number; lastYearChangeAudited?: number; yearOfAssessment?: string | null }>
    } => {
        // WHY: read getState() (not hook closure) so prepareForPersist store writes are visible immediately
        const st = useFsCategoryStore.getState();
        const bsTree = st.bsTree;
        const plTree = st.plTree;
        const bsValues = st.bsValues || {};
        const plValues = st.plValues || {};
        const bsLastYearValues = st.bsLastYearValues || {};
        const plLastYearValues = st.plLastYearValues || {};
        const byYoa = st.plValuesByYoa || {};

        const coerce = (v: any): number => {
            if (v === null || v === undefined || v === '') return 0;
            const n = Number(String(v).replace(/,/g, ''));
            return Number.isFinite(n) ? n : 0;
        };

        const toLeafLines = (
            tree: any[] | null | undefined,
            map: Record<number, string> | null | undefined,
            lastYearMap: Record<number, string> | null | undefined,
            initialLastYearMap: Record<number, string>,
            hasChangeAuditedSet: Set<number>,
            yearOfAssessment?: string | null,
        ) => {
            const ids = new Set<number>();
            const walk = (arr: any[]) => {
                for (const it of arr || []) {
                    const hasChildren = Array.isArray(it?.children) && it.children.length > 0;
                    if (!hasChildren) {
                        const id = Number(it?.fsId ?? it?.id);
                        // Skip compute-only virtual draft fsId (negative)
                        if (!Number.isNaN(id) && id > 0) ids.add(id);
                    } else {
                        walk(it.children);
                    }
                }
            };
            walk((tree as any[]) || []);
            Object.keys(map || {}).forEach(k => {
                const id = Number(k);
                if (!Number.isNaN(id) && id > 0) ids.add(id);
            });

            return Array.from(ids).map(id => {
                const amount = coerce((map as any)?.[id]);
                const currentLastYear = coerce((lastYearMap as any)?.[id]);
                const initialLastYear = coerce(initialLastYearMap[id]);
                const hadChangeAudited = hasChangeAuditedSet.has(id);

                const result: {
                    fsId: number;
                    amount: number;
                    lastYearChangeAudited?: number;
                    yearOfAssessment?: string | null;
                } = {
                    fsId: id,
                    amount
                };

                if (yearOfAssessment != null && yearOfAssessment !== '') {
                    result.yearOfAssessment = yearOfAssessment;
                }

                // Include 0 so COT formula-side cleanup (PUR/COS) persists for Last YOA
                if (hadChangeAudited || currentLastYear !== initialLastYear) {
                    result.lastYearChangeAudited = currentLastYear;
                }

                return result;
            });
        };

        const bsLines = toLeafLines(
            (bsTree as any[]) || [],
            bsValues,
            bsLastYearValues,
            initialLastYearValuesRef.current.bs,
            hasLastYearChangeAuditedRef.current.bs
        );

        let plLines: Array<{
            fsId: number;
            amount: number;
            lastYearChangeAudited?: number;
            yearOfAssessment?: string | null
        }>;

        if (isFsMulti && fsYoaColumns) {
            plLines = [];
            const lastYearOnce = new Set<number>();
            const seenFsYoa = new Set<string>();
            for (const yoa of fsYoaColumns) {
                const yoaKey = String(yoa || '').trim();
                if (!yoaKey) continue;
                const map = byYoa[yoa] || byYoa[yoaKey] || {};
                const chunk = toLeafLines(
                    (plTree as any[]) || [],
                    map,
                    // lastYear only on first YOA chunk to avoid duplicating comparative
                    lastYearOnce.size === 0 ? plLastYearValues : {},
                    initialLastYearValuesRef.current.pl,
                    hasLastYearChangeAuditedRef.current.pl,
                    yoaKey,
                );
                for (const line of chunk) {
                    const dedupeKey = `${line.fsId}|${yoaKey}`;
                    if (seenFsYoa.has(dedupeKey)) continue;
                    seenFsYoa.add(dedupeKey);
                    if (line.lastYearChangeAudited != null) {
                        lastYearOnce.add(line.fsId);
                    }
                    plLines.push(line);
                }
            }
        } else {
            plLines = toLeafLines(
                (plTree as any[]) || [],
                plValues,
                plLastYearValues,
                initialLastYearValuesRef.current.pl,
                hasLastYearChangeAuditedRef.current.pl
            );
        }

        return {bsLines, plLines};
    }, [isFsMulti, fsYoaColumns]);

    const valuesSnapshot = React.useCallback(() => JSON.stringify({
        bs: sBSValues || {},
        pl: sPLValues || {},
        bsLy: sBSLastYearValues || {},
        plLy: sPLLastYearValues || {},
    }), [sBSValues, sPLValues, sBSLastYearValues, sPLLastYearValues]);

    const savedSnapshotRef = React.useRef<string | null>(null);
    const skipDirtyRef = React.useRef(false);

    // Capture baseline once data is ready (or after company/annual switch)
    useEffect(() => {
        if (!dataReady) {
            savedSnapshotRef.current = null;
            return;
        }
        if (savedSnapshotRef.current === null) {
            savedSnapshotRef.current = valuesSnapshot();
        }
    }, [dataReady, valuesSnapshot, companyId, annualId]);

    // Reset baseline when switching company/annual
    useEffect(() => {
        savedSnapshotRef.current = null;
    }, [companyId, annualId]);

    const persistFs = React.useCallback(async (opts?: {silent?: boolean}): Promise<boolean> => {
        try {
            if (!opts?.silent) setUiError(null);
            setUpdating(true);
            // Flush inline custom draft (create + amounts) into store before collecting lines
            await plRef.current?.prepareForPersist?.();
            const {bsLines, plLines} = collectLines();
            await changeFs({
                companyId,
                annualId,
                balanceSheetLines: bsLines,
                profitLossLines: plLines,
                preserveWpFields: true,
            });
            // Drop idle PL warmup so remount / PL tab reopen rebuilds from store (incl. custom leaves)
            clearPlFsWarmup();
            skipDirtyRef.current = true;
            savedSnapshotRef.current = valuesSnapshot();
            if (!opts?.silent) {
                onUpdated?.('Updated successfully');
            }
            return true;
        } catch (e: any) {
            if (!opts?.silent) {
                setUiError(e?.message || 'Update failed');
            }
            return false;
        } finally {
            setUpdating(false);
        }
    }, [collectLines, companyId, annualId, onUpdated, valuesSnapshot]);

    const {
        status: autoSaveStatus,
        markDirty,
        markClean,
        lastSavedAt,
    } = useIdleAutoSave({
        enabled: dataReady,
        onSave: async () => persistFs({silent: true}),
    });

    // Mark dirty when store values diverge from last saved snapshot
    useEffect(() => {
        if (!dataReady || savedSnapshotRef.current === null) return;
        if (skipDirtyRef.current) {
            skipDirtyRef.current = false;
            return;
        }
        if (valuesSnapshot() !== savedSnapshotRef.current) {
            markDirty();
        }
    }, [dataReady, valuesSnapshot, markDirty]);

    /** GL 同款：上传上年 BS/PL → 预览匹配 → Apply 写入 Last Year 列 */
    const handlePriorYearFsExtractApply = React.useCallback((
        extracted: Record<string, number>,
        _result?: unknown,
        remarks?: Record<string, string>,
    ) => {
        const store = useFsCategoryStore.getState();
        const normalized: Record<string, number> = {};
        for (const [k, v] of Object.entries(extracted || {})) {
            const n = Number(v);
            if (k && Number.isFinite(n)) normalized[k] = n;
        }
        const touchedFs = new Set<string>();
        for (const k of Object.keys(normalized)) {
            const i = k.indexOf('|');
            if (i > 0) touchedFs.add(k.slice(0, i));
        }
        for (const fs of Object.keys(remarks || {})) {
            if (fs) touchedFs.add(fs);
        }
        setLastYearRemarkByFsCode((prev) => mergeRemarksReplaceByFs(prev, remarks, touchedFs));

        const {bs: bsMap, pl: plMap, lfpLastYear} = priorToFsLastYearMaps(
            normalized,
            [],
            store.byCode,
            {plTree: store.plTree, bsTree: store.bsTree},
        );
        // Apply 后可能 COS+PUR 同有值；先按零值规则定侧并清公式侧，避免 PL COT sync 爆栈
        const nextBsMerged = {...(store.bsLastYearValues || {}), ...bsMap};
        const nextPlMerged = {...(store.plLastYearValues || {}), ...plMap};
        const lyMode = resolvePlCotInputMode(store.plCotInputMode ?? null, {
            plTree: store.plTree,
            bsTree: store.bsTree,
            plValuesMap: nextPlMerged,
            bsValuesMap: nextBsMerged,
            bsLastYearMap: nextBsMerged,
        });
        const plForStore = zeroPlCotFormulaSideInDataMap(
            nextPlMerged,
            lyMode,
            resolvePlCotFsIds(store.plTree),
        );

        store.setBsLastYearValues(nextBsMerged);
        store.setPlLastYearValues(plForStore);
        store.setBsRetainedEarningsUseStored(true);
        store.setGlSuppressPlAfterTaxLastYear(false);
        store.setPLComputed({afterTaxLastYear: lfpLastYear});
        clearPlFsWarmup();
        setLastYearApplyNonce((n) => n + 1);
        markDirty();
        setImportSnackbar({
            open: true,
            message: `AI extract applied: ${Object.keys(bsMap).length + Object.keys(plMap).length} Last Year line(s)`,
            severity: 'success',
        });
    }, [markDirty]);

    React.useImperativeHandle(ref, () => ({
        getLines: () => {
            try {
                return collectLines();
            } catch {
                return null;
            }
        },
    }), [collectLines]);

    // Stabilize parent callbacks via ref to prevent infinite re-render loops
    // (inline handlers in StepDFs recreate every render and would retrigger effects)
    const onBsErrorChangeRef = React.useRef(onBsErrorChange);
    onBsErrorChangeRef.current = onBsErrorChange;
    const onHideSubmitChangeRef = React.useRef(onHideSubmitChange);
    onHideSubmitChangeRef.current = onHideSubmitChange;

    const handleBsErrorChange = React.useCallback((msg: string | null) => {
        setBsError(msg || null);
        onBsErrorChangeRef.current?.(msg || null);
    }, []);

    const handleBsStatusChange = React.useCallback((s: typeof bsStatus) => {
        setBsStatus((prev) => {
            if (
                prev.netAssets === s.netAssets
                && prev.capitalReserves === s.capitalReserves
                && prev.equal === s.equal
                && prev.zero === s.zero
                && prev.lastYearNetAssets === s.lastYearNetAssets
                && prev.lastYearCapitalReserves === s.lastYearCapitalReserves
                && prev.equalLastYear === s.equalLastYear
                && prev.zeroLastYear === s.zeroLastYear
            ) {
                return prev;
            }
            return s;
        });
    }, []);

    // BS-EQ (capitalReserves) 与 PL-PAT (plAfterTax) 均为 0 时通知父级隐藏 Submit
    React.useEffect(() => {
        if (!onHideSubmitChangeRef.current) return;
        if (plAfterTax === null || plAfterTax === undefined) return;
        onHideSubmitChangeRef.current(bsStatus.capitalReserves === 0 && plAfterTax === 0);
    }, [bsStatus.capitalReserves, plAfterTax]);

    // Mount BS+PL together (renderAll hides inactive). Tab switch = visibility only — no cold PL wait.
    const [visitedTabs, setVisitedTabs] = React.useState<Set<string>>(new Set(['BS', 'PL']));
    const [activeTab, setActiveTab] = React.useState<string>('BS');
    const [, startTabTransition] = React.useTransition();

    // Store 未就绪时清 UI ready；重新挂载后由 onLoadingChange 再点亮
    React.useEffect(() => {
        if (!dataReady) {
            setBsUiReady(false);
            setPlUiReady(false);
            setActiveTab('BS');
        }
    }, [dataReady, companyId, annualId]);

    const handleBsLoadingChange = React.useCallback((isLoading: boolean) => {
        setBsUiReady(!isLoading);
    }, []);

    const handlePlLoadingChange = React.useCallback((isLoading: boolean) => {
        setPlUiReady(!isLoading);
    }, []);

    // PL 未就绪时禁止停在 PL tab
    React.useEffect(() => {
        if (!plUiReady && activeTab === 'PL') {
            setActiveTab('BS');
        }
    }, [plUiReady, activeTab]);

    // Keep PL init cache warm for YOA / value churn (PL already mounted with BS).
    React.useEffect(() => {
        if (loading) return;
        if (!sPLTree || !Array.isArray(sPLTree) || sPLTree.length === 0) return;

        const plTree = sPLTree;
        const yoaCols = fsYoaColumns;
        const build = () => {
            const st = useFsCategoryStore.getState();
            return buildPlFsInitSnapshot({
                annualId,
                plTreeFlat: plTree,
                plValues: st.plValues,
                plValuesByYoa: st.plValuesByYoa,
                plLastYearValues: st.plLastYearValues,
                bsTree: st.bsTree,
                bsValues: st.bsValues,
                bsLastYearValues: st.bsLastYearValues,
                cotInputMode: st.plCotInputMode ?? 'COS',
                fsYoaColumns: yoaCols,
            });
        };

        const st = useFsCategoryStore.getState();
        const sig = buildPlFsWarmupSignature({
            annualId,
            fsYoaColumns: yoaCols,
            plTreeLen: countFsTreeNodes(plTree),
            plValues: st.plValues,
            plValuesByYoa: st.plValuesByYoa,
            plLastYearValues: st.plLastYearValues,
            bsValues: st.bsValues,
            bsLastYearValues: st.bsLastYearValues,
            cotInputMode: st.plCotInputMode ?? 'COS',
        });
        if (!peekPlFsWarmup(sig)) {
            schedulePlFsWarmup(build, 0);
        }
    // 勿依赖 sPLValues：COT sync 每次写回会重跑 warmup，加重 PL 更新环
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, sPLTree, plValuesByYoa, fsYoaColumns, annualId]);

    // Tabs already pre-mounted; keep visited set in sync for any future tab keys.
    const handleTabChange = React.useCallback((key: string) => {
        if (key === 'PL' && !plUiReady) return;
        if (key === 'PL') {
            cancelPendingPlFsWarmup();
        }
        const apply = () => {
            setActiveTab(key);
            setVisitedTabs(prev => new Set([...prev, key]));
        };
        if (visitedTabs.has(key)) {
            apply();
            return;
        }
        startTabTransition(apply);
    }, [visitedTabs, startTabTransition, plUiReady]);

    const plTabLoadingContent = React.useMemo(() => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            height: '100%',
            boxSizing: 'border-box',
            minHeight: 0,
        }}>
            <TableContainer component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                <Table size="small" sx={{tableLayout: 'fixed', width: '100%'}}>
                    <TableBody>
                        <FsStatementTableSkeletonRows
                            columns={buildFsPlSkeletonColumns({
                                hideLastYearColumns: firstYearAudit,
                                yoaCount: Array.isArray(fsYoaColumns) ? fsYoaColumns.length : 0,
                            })}
                        />
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    ), [firstYearAudit, fsYoaColumns]);

    // Option 3: validation ready only after store has PL-PAT (from init / recompute)
    const validationReady = !loading && plAfterTax !== null && plAfterTax !== undefined;

    // Memoize individual tab content to prevent re-renders
    const bsContent = React.useMemo(() => {
        if (!visitedTabs.has('BS')) {
            return (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                    <Typography variant="body2" color="text.secondary">Loading Balance Sheet...</Typography>
                </Box>
            );
        }
        return (
            <BalanceSheet
                mode="fs"
                annualId={annualId}
                hideLastYearColumns={firstYearAudit}
                lastYearRemarkByFsCode={lastYearRemarkByFsCode}
                lastYearApplyNonce={lastYearApplyNonce}
                ref={bsRef as React.MutableRefObject<BalanceSheetHandle | null>}
                onErrorChange={handleBsErrorChange}
                onStatusChange={handleBsStatusChange}
                onLoadingChange={handleBsLoadingChange}
            />
        );
    }, [
        annualId, handleBsErrorChange, handleBsStatusChange, handleBsLoadingChange,
        visitedTabs.has('BS'), firstYearAudit, lastYearRemarkByFsCode, lastYearApplyNonce,
    ]);

    const plContent = React.useMemo(() => {
        if (!visitedTabs.has('PL')) {
            return plTabLoadingContent;
        }
        return (
            <ProfitLoss
                key="fs-detail-pl"
                mode="fs"
                companyId={companyId}
                annualId={annualId}
                hideLastYearColumns={firstYearAudit}
                lastYearRemarkByFsCode={lastYearRemarkByFsCode}
                lastYearApplyNonce={lastYearApplyNonce}
                fsYoaColumns={fsYoaColumns}
                ref={plRef as React.MutableRefObject<ProfitLossHandle | null>}
                onLoadingChange={handlePlLoadingChange}
            />
        );
    }, [
        companyId, annualId, visitedTabs.has('PL'), firstYearAudit, fsYoaColumns,
        plTabLoadingContent, handlePlLoadingChange, lastYearRemarkByFsCode, lastYearApplyNonce,
    ]);

    // Create tabs array - now stable and won't recreate on every visitedTabs change
    const tabs = React.useMemo(() => ([
        {
            key: 'BS',
            label: 'Balance Sheet',
            icon: <BalanceSheetOutlinedIcon sx={{fontSize: 24}} />,
            content: bsContent,
        },
        {
            key: 'PL',
            label: 'Income Statement',
            icon: <ProfitLossOutlinedIcon sx={{fontSize: 24}} />,
            content: plContent,
            disabled: !plUiReady,
            disabledTooltip: 'Loading Income Statement...',
        },
    ]), [bsContent, plContent, plUiReady]);

    // update button control
    const handleUpdate = React.useCallback(async () => {
        const ok = await persistFs({silent: false});
        if (ok) markClean();
    }, [persistFs, markClean]);

    // unified cancel handler to avoid ReferenceError in closures
    const handleClose = React.useCallback(() => {
        try {
            cancelInFlight();
        } catch {
        }
        try {
            onRequestListRefresh?.();
        } catch {
        }
        try {
            onCloseProp?.();
        } catch {
        }
    }, [cancelInFlight, onCloseProp, onRequestListRefresh]);

    // submit button control
    const handleConfirmSubmit = React.useCallback(async () => {
        setSubmitAnchorPos(null);
        try {
            setUiError(null);
            setUpdating(true);
            await plRef.current?.prepareForPersist?.();
            const {bsLines, plLines} = collectLines();
            await changeFs({
                companyId,
                annualId,
                balanceSheetLines: bsLines,
                profitLossLines: plLines,
                preserveWpFields: true,
            });
            await changeStatus(companyId, annualId, AnnualFsStatusEnum.AUDIT_ADJUSTMENT);
            skipDirtyRef.current = true;
            savedSnapshotRef.current = valuesSnapshot();
            markClean();
            onUpdated?.('Submitted successfully');
        } catch (e: any) {
            setUiError(e?.message || 'Submit failed');
        } finally {
            setUpdating(false);
        }
    }, [collectLines, companyId, annualId, onUpdated, valuesSnapshot, markClean]);

    // Import Excel handler
    const handleImportExcel = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reset file input so same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';

        // Confirm dialog
        const confirmed = window.confirm('Import will overwrite current unsaved BS/PL values. Continue?');
        if (!confirmed) return;

        try {
            setImporting(true);
            setUiError(null);
            const result: ExcelImportResult = await importExcel(file, companyId);
            // Force reload FS tree with new importToken so aliases get persisted and names refreshed
            if (result.importToken && companyId && annualId) {
                useFsCategoryStore.getState().reset();
                await useFsCategoryStore.getState().initIfNeeded(companyId, annualId, result.importToken);
            } else {
                useFsCategoryStore.getState().applyImportResult(result);
            }
            const msg = `Import completed: BS ${result.bsMatchedCount} matched / ${result.bsSkippedCount} skipped, PL ${result.plMatchedCount} matched / ${result.plSkippedCount} skipped`;
            setImportSnackbar({open: true, message: msg, severity: 'success'});
            // Import overwrites local values — treat as dirty until Update/auto-save
            markDirty();
        } catch (err: any) {
            setImportSnackbar({open: true, message: err?.message || 'Import failed', severity: 'error'});
        } finally {
            setImporting(false);
        }
    }, [companyId, annualId, markDirty]);

    const shellSkeleton = (
        <FsDetailShellSkeleton
            height={contentHeight}
            flex={isDrawerMode ? 1 : undefined}
            minHeight={isDrawerMode ? 0 : undefined}
        />
    );

    // UI
    const content = (
        error ? (
            <Alert severity="error" sx={{width: '50%'}}>{error}</Alert>
        ) : (
            <Box sx={{
                height: contentHeight,
                flex: isDrawerMode ? 1 : undefined,
                minHeight: isDrawerMode ? 0 : undefined,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                overflow: 'hidden',
            }}>
                <SplitTabsPanel
                    tabs={tabs}
                    height={tabsHeight}
                    selectedKey={activeTab}
                    onChange={handleTabChange}
                    renderAll={true}
                    preserveInactivePaneLayout={true}
                    sx={isDrawerMode ? {flex: 1, minHeight: 0} : undefined}
                    contentWrapperSx={{overflow: 'hidden'}}
                    rightPaneSx={{overflow: 'hidden'}}
                    headerRight={
                        !firstYearAudit ? (
                            <PriorYearFsExtractBar
                                onApply={handlePriorYearFsExtractApply}
                                disabled={!dataReady || loading || !!error}
                                expectedPeriod={expectedLastYearPeriod}
                                companyId={companyId}
                                variant="fs"
                            />
                        ) : null
                    }
                    errorMsg={validationReady && bsError && (
                        (!bsStatus.equal && !bsStatus.zero) ||
                        (!firstYearAudit && bsStatus.equalLastYear === false && bsStatus.zeroLastYear === false)
                    ) ? bsError : null}
                />
            </Box>
        )
    );

    if (!open) {
        return <>
            <Box sx={{position: 'relative', width: '100%', minHeight: contentHeight}}>
                {/* error 或 store 就绪后挂载；龙骨挡到 BS UI ready（error 时不挡） */}
                {(error || dataReady) && (
                    <Fade in={!!error || shellReady} timeout={300}>
                        <Box sx={{visibility: (error || shellReady) ? 'visible' : 'hidden'}}>{content}</Box>
                    </Fade>
                )}
                {!shellReady && !error && (
                    <Box sx={{
                        position: dataReady ? 'absolute' : 'relative',
                        inset: dataReady ? 0 : undefined,
                        width: '100%',
                        zIndex: 1,
                        bgcolor: 'background.paper',
                    }}>
                        {shellSkeleton}
                    </Box>
                )}
            </Box>
            {shellReady && (
                <Box sx={{display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5}}>
                    <AutoSaveStatus status={autoSaveStatus} lastSavedAt={lastSavedAt}/>
                </Box>
            )}
            <Snackbar
                open={importSnackbar.open}
                autoHideDuration={6000}
                onClose={() => setImportSnackbar(prev => ({...prev, open: false}))}
                message={importSnackbar.message}
            />
        </>;
    }

    return (<>
        <Drawer
            anchor="right"
            open={open}
            onClose={(_, reason) => {
                // 与原 Dialog disableEscapeKeyDown 行为一致：Esc 不关闭
                if (reason === 'escapeKeyDown') return;
                handleClose();
            }}
            PaperProps={{
                sx: {
                    width: drawerWidthPx,
                    maxWidth: '95vw',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    // MULTI: avoid width animation thrash when YOA columns / content settle
                    transition: isFsMulti
                        ? 'none'
                        : (theme: any) => theme.transitions.create('width', {
                            easing: theme.transitions.easing.easeInOut,
                            duration: theme.transitions.duration.shorter,
                        }),
                },
            }}
        >
            <Box sx={{
                px: 2,
                py: 1.5,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
            }}>
                <Typography variant="h6" sx={{fontWeight: 600}}>
                    Finance Statement Details
                </Typography>
            </Box>
            <Box sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
            }}>
                <Box
                    ref={contentMeasureRef}
                    sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2,
                    overflow: 'hidden',
                    // 节点名按最长单行宽度适配：取消百分比列宽限制，禁止折行
                    '& table': {
                        width: 'max-content',
                        minWidth: '100%',
                        tableLayout: 'auto',
                    },
                    '& .MuiTableCell-root:first-of-type': {
                        width: 'auto !important',
                        minWidth: 'auto !important',
                        maxWidth: 'none !important',
                        whiteSpace: 'nowrap',
                    },
                    // fs 模式金额列：SINGLE = col2–4；MULTI = YOA×N + Last Year + Change
                    ...amountColumnCss,
                    // MULTI 下 Input 被拉满列宽；必须显式 textAlign，否则金额视觉靠左
                    '& .MuiTableCell-root:nth-of-type(n+2)': {
                        textAlign: 'center',
                    },
                    '& .MuiTableCell-root:nth-of-type(n+2) .MuiInputBase-root': {
                        width: '100%',
                    },
                    '& .MuiTableCell-root:nth-of-type(n+2) .MuiOutlinedInput-input': {
                        minWidth: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                    },
                    '& .MuiTableCell-root:nth-of-type(n+2) .MuiInputBase-input': {
                        textAlign: 'center',
                    },
                }}>
                    <Box sx={{
                        position: 'relative',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}>
                        {/* error 或 store 就绪后挂载；龙骨覆盖至 BS UI ready */}
                        {(error || dataReady) && (
                            <Fade
                                in={!!error || shellReady}
                                timeout={300}
                                style={{display: 'flex', flex: 1, minHeight: 0, width: '100%'}}
                            >
                                <Box sx={{
                                    width: '100%',
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    visibility: (error || shellReady) ? 'visible' : 'hidden',
                                }}>
                                    {content}
                                </Box>
                            </Fade>
                        )}
                        {!shellReady && !error && (
                            <Box sx={{
                                position: dataReady ? 'absolute' : 'relative',
                                inset: dataReady ? 0 : undefined,
                                flex: dataReady ? undefined : 1,
                                minHeight: 0,
                                width: '100%',
                                zIndex: 1,
                                bgcolor: 'background.paper',
                            }}>
                                {shellSkeleton}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                borderTop: theme => `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
            }}>
                {shellReady && (
                    <>
                        <AutoSaveStatus status={autoSaveStatus} lastSavedAt={lastSavedAt}/>
                        <Button onClick={handleClose}>Cancel</Button>
                        {validationReady && !hideUpdateButton && !bsStatus.zero && (
                            !bsStatus.equal || (!firstYearAudit && bsStatus.equalLastYear === false && bsStatus.zeroLastYear === false)
                        ) && (
                            <Button
                                variant="contained"
                                disabled={updating}
                                onClick={handleUpdate}
                            >
                                {updating ? 'Updating...' : 'Update'}
                            </Button>
                        )}
                        {validationReady && !hideUpdateButton && !bsStatus.zero && bsStatus.equal && (
                            firstYearAudit || bsStatus.equalLastYear !== false || bsStatus.zeroLastYear === true
                        ) && (
                            <Button
                                variant="outlined"
                                disabled={updating}
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setSubmitAnchorPos({
                                        top: rect.top,
                                        left: rect.left + rect.width / 2,
                                    });
                                }}
                            >
                                Submit
                            </Button>
                        )}
                    </>
                )}
            </Box>
            <Popover
                open={submitAnchorPos != null}
                anchorReference="anchorPosition"
                anchorPosition={submitAnchorPos ?? {top: 0, left: 0}}
                onClose={() => setSubmitAnchorPos(null)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
                PaperProps={{sx: {p: 2, width: 420}}}
            >
                <Alert severity="warning" sx={{mb: 1}}>
                    Is the company's financial report entering the audit phase?
                </Alert>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1}}>
                    <Button onClick={() => setSubmitAnchorPos(null)}>No</Button>
                    <Button variant="contained" disabled={updating} onClick={handleConfirmSubmit}>
                        Yes
                    </Button>
                </Box>
            </Popover>
        </Drawer>
        <Snackbar
            open={importSnackbar.open}
            autoHideDuration={6000}
            onClose={() => setImportSnackbar(prev => ({...prev, open: false}))}
            message={importSnackbar.message}
        />
    </>);
});
